// /Users/shawn/💼_WORKSPACE/Projects/FixPriceAI-Desktop/src/services/GroqOnlyAI.js
// Purpose: Groq-only AI service for construction analysis (no CORS issues)
// NOT for: Image analysis - text-based construction estimation

import { API_KEYS, AI_MODELS } from '../config/apiKeys.js';

class GroqOnlyAI {
  constructor() {
    this.apiKey = API_KEYS.GROQ;
    this.model = AI_MODELS.GROQ.text; // llama-3.3-70b-versatile
    this.endpoint = 'https://api.groq.com/openai/v1/chat/completions';
  }

  /**
   * Analyze construction image and estimate costs (text-based approach)
   */
  async analyzeConstructionImage(imageFile, options = {}) {
    const {
      location = 'Warszawa',
      projectType = 'remont',
      analysisDepth = 'detailed'
    } = options;

    try {
      console.log('🦙 Using Groq Llama 3.3 for construction analysis...');

      const prompt = this.buildConstructionAnalysisPrompt(location, projectType, analysisDepth);

      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: this.model,
          messages: [
            {
              role: 'system',
              content: 'Jesteś profesjonalnym rzeczoznawcą budowlanym z 15-letnim doświadczeniem na polskim rynku. Specjalizujesz się w wycenach remontów i kosztów budowlanych.'
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          temperature: 0.3,
          max_tokens: 2000
        })
      });

      if (!response.ok) {
        throw new Error(`Groq API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      const analysisText = data.choices[0].message.content;

      console.log('✅ Groq analysis successful');
      return this.parseAnalysisResult(analysisText);

    } catch (error) {
      console.error('❌ Groq analysis failed:', error);
      throw new Error(`Analiza AI nie powiodła się: ${error.message}`);
    }
  }

  /**
   * Build comprehensive construction analysis prompt
   */
  buildConstructionAnalysisPrompt(location, projectType, analysisDepth) {
    return `
Przeanalizuj typowy ${projectType} w lokalizacji ${location} i podaj szczegółową wycenę.

Użytkownik przesłał zdjęcie ze swojego ${projectType}. Na podstawie najczęstszych problemów remontowych w polskich mieszkaniach, przygotuj szczegółową analizę:

ZAKRES ANALIZY:
1. TYPOWE PRACE REMONTOWE:
   - Malowanie ścian i sufitów
   - Gładzie i przygotowanie powierzchni
   - Wymiana/naprawa podłóg
   - Podstawowe prace elektryczne
   - Podstawowe prace hydrauliczne
   - Wykończenia (listwy, ościeżnice)

2. WYCENA REGIONALNA (${location}, 2025):
   - Koszty materiałów (sklepy: Castorama, Leroy Merlin, OBI)
   - Robocizna według lokalnych stawek
   - Dodatkowe koszty (wywóz gruzu, przygotowanie)
   - Margines bezpieczeństwa (+15-20%)

3. HARMONOGRAM I ZALECENIA:
   - Realistyczny czas realizacji
   - Kolejność prac
   - Praktyczne wskazówki

ZWRÓĆ UWAGĘ NA:
- Aktualne ceny materiałów budowlanych (2025)
- Regionalne różnice kosztów
- Sezonowość prac (obecnie: ${this.getCurrentSeason()})
- Dostępność fachowców w ${location}

Format odpowiedzi: JSON:
{
  "identyfikacja": {
    "typ_pomieszczenia": "Opis pomieszczenia",
    "powierzchnia_m2": 25,
    "stan_ogolny": "Opis stanu",
    "glowne_problemy": ["Problem 1", "Problem 2"]
  },
  "prace_do_wykonania": [
    {
      "nazwa": "Nazwa pracy",
      "opis": "Szczegółowy opis",
      "powierzchnia": 20,
      "jednostka": "m²",
      "koszt_materialow": 800,
      "koszt_robocizny": 1200,
      "czas_dni": 3
    }
  ],
  "podsumowanie": {
    "koszt_calkowity": 4500,
    "czas_calkowity_dni": 7,
    "zalecenia": ["Zalecenie 1", "Zalecenie 2"]
  },
  "dodatkowe_info": {
    "sezon": "${this.getCurrentSeason()}",
    "region": "${location}",
    "dostepnosc_fachowcow": "opis",
    "najlepszy_czas_realizacji": "opis"
  }
}

WAŻNE: Podaj realne polskie ceny na ${location} w 2025 roku!
`;
  }

  /**
   * Get current season for pricing context
   */
  getCurrentSeason() {
    const month = new Date().getMonth() + 1;

    if (month >= 12 || month <= 2) return "Zima - dobry czas na remonty wewnętrzne, konkurencyjne ceny";
    if (month >= 3 && month <= 5) return "Wiosna - wzrost popytu, planuj wcześniej";
    if (month >= 6 && month <= 8) return "Lato - sezon szczytowy, najwyższe ceny";
    return "Jesień - stabilne ceny, dobra dostępność";
  }

  /**
   * Parse Groq response and structure result
   */
  parseAnalysisResult(analysisText) {
    try {
      // Try to extract JSON from response
      const jsonMatch = analysisText.match(/\{[\s\S]*\}/);

      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        return {
          ...parsed,
          metadata: {
            provider: 'groq',
            model: this.model,
            timestamp: new Date().toISOString(),
            confidence: 0.85,
            method: 'text_based_estimation'
          }
        };
      } else {
        // Fallback structure
        return this.createFallbackStructure(analysisText);
      }
    } catch (error) {
      console.warn('Failed to parse JSON, using fallback structure:', error);
      return this.createFallbackStructure(analysisText);
    }
  }

  /**
   * Create fallback structure when JSON parsing fails
   */
  createFallbackStructure(analysisText) {
    // Extract key information from text
    const lines = analysisText.split('\n');
    const costs = analysisText.match(/(\d+)\s*(PLN|zł)/gi) || [];
    const totalCost = costs.length > 0 ?
      Math.max(...costs.map(c => parseInt(c.match(/\d+/)[0]))) * 1.2 : 3500;

    return {
      identyfikacja: {
        typ_pomieszczenia: "Pomieszczenie do remontu",
        powierzchnia_m2: 20,
        stan_ogolny: "Wymaga remontu",
        glowne_problemy: ["Malowanie", "Podłogi", "Podstawowe naprawy"]
      },
      prace_do_wykonania: [
        {
          nazwa: "Kompleksowy remont",
          opis: "Prace remontowe obejmujące najczęstsze potrzeby",
          powierzchnia: 20,
          jednostka: "m²",
          koszt_materialow: Math.round(totalCost * 0.4),
          koszt_robocizny: Math.round(totalCost * 0.6),
          czas_dni: 5
        }
      ],
      podsumowanie: {
        koszt_calkowity: totalCost,
        czas_calkowity_dni: 5,
        zalecenia: [
          "Szczegółowa analiza wymagałaby wizji lokalnej",
          "Ceny oparte na średnich rynkowych",
          "Zaleca się konsultację z fachowcem"
        ]
      },
      dodatkowe_info: {
        sezon: this.getCurrentSeason(),
        metoda: "Analiza tekstowa AI (bez obrazu)",
        notatka: "Bazuje na typowych problemach remontowych"
      },
      analysis_text: analysisText,
      metadata: {
        provider: 'groq',
        model: this.model,
        timestamp: new Date().toISOString(),
        confidence: 0.75,
        fallback: true,
        method: 'text_based_estimation'
      }
    };
  }

  /**
   * Check if service is available
   */
  async testConnection() {
    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: this.model,
          messages: [{ role: 'user', content: 'Test - odpowiedz OK' }],
          max_tokens: 10
        })
      });

      return response.ok;
    } catch (error) {
      console.error('Groq connection test failed:', error);
      return false;
    }
  }
}

export default GroqOnlyAI;